// ─────────────────────────────────────────────────────────────
// LE CIEL — cycle jour/nuit et météo
//
// Un cycle complet (lever → midi → coucher → nuit → lever) dure 15
// minutes réelles. L'horloge est calée sur l'heure réelle, pas sur la
// durée de la session : deux onglets ouverts voient la même heure.
//
// ── CE QUI A CHANGÉ : les transitions étaient abruptes ────────
// Deux causes, pas une :
//   1. seulement 4 instants clés, donc des sauts de teinte visibles
//      entre midi et le coucher ;
//   2. surtout : la couleur de la MER était choisie par « bucket »
//      (jour / crépuscule / nuit), c'est-à-dire par PALIER. À la
//      seconde où le bucket basculait, tout l'océan changeait de
//      couleur d'un coup. C'était ça, la vraie cassure.
//
// Maintenant : 10 instants clés, interpolation adoucie (smoothstep,
// pente nulle aux extrémités — donc aucun « angle » perceptible au
// passage d'un instant au suivant), et la palette de la mer est
// interpolée canal par canal comme le reste. Plus aucun palier.
// ─────────────────────────────────────────────────────────────

export const CYCLE_MS = 15 * 60 * 1000;
const DAY_EPOCH = Date.UTC(2026, 0, 1); // origine arbitraire mais fixe

// phase ∈ [0,1) : 0 = lever du soleil, 0.25 = midi, 0.5 = coucher, 0.75 = minuit
export function currentPhase(overrideMs) {
  const ms = overrideMs ?? (Date.now() - DAY_EPOCH);
  return ((ms % CYCLE_MS) + CYCLE_MS) % CYCLE_MS / CYCLE_MS;
}

export function currentDayIndex(overrideMs) {
  const ms = overrideMs ?? (Date.now() - DAY_EPOCH);
  return Math.floor(ms / CYCLE_MS);
}

// Suite déterministe d'intervalles 5–10 : le jour de pluie n+1 est
// toujours 5 à 10 jours après le jour de pluie n.
const hash01 = (n) => {
  const h = Math.imul(n ^ 0x9e3779b9, 2654435761) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
};
export function isRainyDay(dayIndex) {
  let day = 0, guard = 0;
  while (day < dayIndex && guard++ < 100000) {
    day += 5 + Math.floor(hash01(day) * 6); // pas de 5 à 10
  }
  return day === dayIndex;
}

// ── Palettes de mer, attachées aux instants clés ──────────────
const SEA_DAY   = { deep: "#14608a", mid: "#2f8fb8", shallow: "#7fd4e8", foam: "#ffffff", glint: "#e8fbff" };
const SEA_GOLD  = { deep: "#123a58", mid: "#2a6d92", shallow: "#6fb9cf", foam: "#ffe6c8", glint: "#ffd9a8" };
const SEA_DUSK  = { deep: "#0d2a44", mid: "#1e4f72", shallow: "#4d90ad", foam: "#f0d2bb", glint: "#dcb894" };
const SEA_NIGHT = { deep: "#08182b", mid: "#14304f", shallow: "#2f5f80", foam: "#c8d8f0", glint: "#9fc4e8" };
const SEA_DAWN  = { deep: "#103c5c", mid: "#22597e", shallow: "#5aa5c2", foam: "#ffe9d6", glint: "#ffd2ad" };

// ── Trame de lumière ──────────────────────────────────────────
//   grade / gradeA   teinte multipliée sur toute la scène, et sa force
//   haze             brume basse [r,g,b,a]
//   vignette         assombrissement des bords
//   lamp             intensité des halos de lampes
//   night            0 = plein jour … 1 = nuit noire (oiseaux, nuages)
//   sea              palette de la mer
const KEYFRAMES = [
  { phase: 0.00, grade: ["#ff9d5c", "#4a4f8f"], gradeA: 0.34, haze: [255, 190, 145, 0.17], vignette: 0.36, lamp: 0.62, night: 0.42, sea: SEA_DAWN },
  { phase: 0.07, grade: ["#ffd9a8", "#7f93c0"], gradeA: 0.20, haze: [255, 216, 178, 0.18], vignette: 0.28, lamp: 0.30, night: 0.18, sea: SEA_GOLD },
  { phase: 0.16, grade: ["#fff3dd", "#cfe4f2"], gradeA: 0.07, haze: [226, 240, 248, 0.18], vignette: 0.22, lamp: 0.14, night: 0.05, sea: SEA_DAY },
  { phase: 0.25, grade: ["#ffffff", "#ffffff"], gradeA: 0.00, haze: [200, 232, 245, 0.18], vignette: 0.20, lamp: 0.10, night: 0.00, sea: SEA_DAY },
  { phase: 0.36, grade: ["#ffeccd", "#d9e6f2"], gradeA: 0.08, haze: [236, 232, 226, 0.18], vignette: 0.23, lamp: 0.16, night: 0.06, sea: SEA_DAY },
  { phase: 0.44, grade: ["#ffbe7d", "#6c74a4"], gradeA: 0.24, haze: [255, 214, 168, 0.20], vignette: 0.33, lamp: 0.55, night: 0.26, sea: SEA_GOLD },
  { phase: 0.50, grade: ["#ffb066", "#3b4380"], gradeA: 0.42, haze: [255, 196, 146, 0.22], vignette: 0.42, lamp: 0.95, night: 0.48, sea: SEA_GOLD },
  { phase: 0.60, grade: ["#a4779c", "#212f5c"], gradeA: 0.55, haze: [176, 160, 190, 0.20], vignette: 0.50, lamp: 1.15, night: 0.76, sea: SEA_DUSK },
  { phase: 0.75, grade: ["#54689f", "#0d1730"], gradeA: 0.64, haze: [96, 128, 196, 0.18],  vignette: 0.58, lamp: 1.30, night: 1.00, sea: SEA_NIGHT },
  { phase: 0.90, grade: ["#6b6ba0", "#141d3c"], gradeA: 0.54, haze: [140, 146, 200, 0.18], vignette: 0.50, lamp: 1.10, night: 0.78, sea: SEA_NIGHT },
];

const lerp = (a, b, u) => a + (b - a) * u;
// Adoucissement : pente nulle aux deux bouts. C'est ce qui supprime la
// cassure perçue au passage d'un instant clé au suivant, même quand les
// couleurs étaient déjà interpolées linéairement.
const smooth = (u) => u * u * (3 - 2 * u);

const parseHex = (h) => {
  const m = String(h).replace("#", "").match(/.{2}/g);
  if (!m || m.length < 3) return [0, 0, 0];
  return m.slice(0, 3).map((x) => parseInt(x, 16) || 0);
};
const toHex = (arr) =>
  "#" + arr.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
const lerpColor = (a, b, u) => {
  const pa = parseHex(a), pb = parseHex(b);
  return toHex(pa.map((v, i) => lerp(v, pb[i], u)));
};
const lerpSea = (a, b, u) => ({
  deep: lerpColor(a.deep, b.deep, u),
  mid: lerpColor(a.mid, b.mid, u),
  shallow: lerpColor(a.shallow, b.shallow, u),
  foam: lerpColor(a.foam, b.foam, u),
  glint: lerpColor(a.glint, b.glint, u),
});

const bucketOf = (k) => (k.night > 0.66 ? "nuit" : k.night > 0.2 ? "crepuscule" : "jour");

export function skyAt(phase) {
  const p = ((phase % 1) + 1) % 1;
  const n = KEYFRAMES.length;

  // Encadrement [a,b] autour de `phase`, en bouclant par-dessus minuit.
  let a, b, span, u;
  if (p < KEYFRAMES[0].phase) {
    a = KEYFRAMES[n - 1];
    b = KEYFRAMES[0];
    span = 1 - a.phase + b.phase;
    u = (p + 1 - a.phase) / span;
  } else {
    a = KEYFRAMES[n - 1];
    b = KEYFRAMES[0];
    span = 1 - a.phase + b.phase;
    u = (p - a.phase) / span;
    for (let k = 0; k < n - 1; k++) {
      if (p >= KEYFRAMES[k].phase && p < KEYFRAMES[k + 1].phase) {
        a = KEYFRAMES[k];
        b = KEYFRAMES[k + 1];
        span = b.phase - a.phase;
        u = (p - a.phase) / span;
        break;
      }
    }
  }
  const s = smooth(Math.max(0, Math.min(1, u)));

  const haze = a.haze.map((v, idx) => lerp(v, b.haze[idx], s));

  return {
    // conservé pour compatibilité : ce n'est PLUS lui qui décide de la
    // couleur de la mer — c'est justement ce palier qui sautait aux yeux.
    bucket: s < 0.5 ? bucketOf(a) : bucketOf(b),
    night: lerp(a.night, b.night, s),
    grade: [lerpColor(a.grade[0], b.grade[0], s), lerpColor(a.grade[1], b.grade[1], s)],
    gradeAlpha: lerp(a.gradeA, b.gradeA, s),
    haze: `rgba(${haze[0].toFixed(0)},${haze[1].toFixed(0)},${haze[2].toFixed(0)},${haze[3].toFixed(3)})`,
    vignette: lerp(a.vignette, b.vignette, s),
    lamp: lerp(a.lamp, b.lamp, s),
    sea: lerpSea(a.sea, b.sea, s),
  };
}

// ── Pluie ─────────────────────────────────────────────────────
export function makeRain(count = 90) {
  const drops = [];
  for (let i = 0; i < count; i++)
    drops.push({ x: Math.random(), y: Math.random(), len: 14 + Math.random() * 18, sp: 5 + Math.random() * 4 });
  return drops;
}

export function drawRain(ctx, drops, W, H, dt, intensity) {
  ctx.save();
  ctx.strokeStyle = "rgba(200,220,240,0.5)";
  ctx.lineWidth = Math.max(1, W / 900);
  ctx.lineCap = "round";
  ctx.globalAlpha = intensity;
  for (const d of drops) {
    d.y += d.sp * dt * 0.06;
    d.x -= 0.06 * dt * 0.06;
    if (d.y > 1.05) { d.y = -0.05; d.x = Math.random(); }
    if (d.x < -0.05) d.x = 1.05;
    const x = d.x * W, y = d.y * H;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - d.len * 0.3, y + d.len);
    ctx.stroke();
  }
  ctx.restore();
}
